import { fabric } from 'fabric';

import { CORNER_RADIUS_SIGN, INCH_QUOTE, MAX_IMAGE_EXPORT_BYTES, MAX_IMAGE_EXPORT_DIMENSION } from '@common/constants';
import { COMING_SOON_IMAGE } from '@common/utils';

import { getCanvasBoundingBox } from './shape-helper';

/**
 * format a radius corner label based upon the size of the radius
 * @param radius
 */
export const formatCornerRadiusLabel = (radius: number): string => {
    // note the leading space - ugh!
    return ` ${CORNER_RADIUS_SIGN} ${radius.toString()} ${INCH_QUOTE}`;
};

/**
 * @DEPRECATED use formatCornerRadiusLabel(radiusSize)
 *  this previously derived a default value based upon the name of a product
 * @param cornerName the name of the corner product - very fragile
 */
export const getCornerRadiusLabel = (cornerName: string): string => {
    let lbl = '';
    switch (cornerName) {
        case 'Radius above 42 in.':
            lbl = '42';
            break;
        case 'Radius up to 42 in.':
            lbl = '42';
            break;
        case 'Radius up to 30 in.':
            lbl = '30';
            break;
        case 'Radius up to 18 in.':
            lbl = '18';
            break;
        case 'Radius up to 6 in.':
            lbl = '6';
            break;
        case 'Radius ¾ inches':
            lbl = '0.75';
            break;
        default:
            lbl = '0.75';
            break;
    }
    return ` ${CORNER_RADIUS_SIGN} ${lbl} ${INCH_QUOTE}`;
};

/**
 * From the current canvas, generate a JPG or PNG sized to fit the entire contents of the drawing
 * to a maximum dimension (either width or height, whichever fits best) and a maximum file size
 * @param canvas
 * @param makePNG
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export const generateCanvasJpeg = (canvas: fabric.Canvas, makePNG = false): string => {
    const box = getCanvasBoundingBox(canvas);
    let leftOffset = 0;
    let topOffset = 0;
    let width = box.getWidth();
    let height = box.getHeight();

    if (canvas.vptCoords && canvas.viewportTransform) {
        leftOffset = (box.left - canvas.vptCoords.tl.x) * canvas.viewportTransform[0];
        topOffset = (box.top - canvas.vptCoords.tl.y) * canvas.viewportTransform[3];
        width = width * canvas.viewportTransform[0];
        height = height * canvas.viewportTransform[3];
    }

    /**
     * Given a current maximum dimension and a current "file size", proportionally scale to attempt to fit a maxFileSize
     * without increasing the dimension in size
     * @param currentMaxDimension current maximum dimension (width or height)
     * @param currentFileSize currrent file size - if <= maxRawImageSize then no change
     * @param maxRawImageSize maximum file size target
     * @return new maxDimension - will never get larger
     */
    const scaleMaxDimension = (currentMaxDimension: number, currentFileSize: number, maxRawImageSize: number): number =>
        currentFileSize > 0 && currentFileSize > maxRawImageSize
            ? currentMaxDimension * (maxRawImageSize / currentFileSize)
            : currentMaxDimension;

    /**
     * calculate the scaling value based on the smaller of the maxDimension to width and height ratio
     * @param width actual canvas width
     * @param height actual canvas height
     * @param maxDimension maximum target dimension in either direction
     */
    const dimensionScaler = (width: number, height: number, maxDimension: number): number =>
        width && height ? Math.min(maxDimension / width, maxDimension / height) : 1;

    // save current background and change to white for rendering (transparent appears black)
    const oldBackGroundColor = canvas.backgroundColor;
    canvas.setBackgroundColor('white', () => undefined);

    let url = '';
    let retryAttempts = 3; // one is normal, two for extremes, three is for edge cases, 5 is way out
    let maxDimension = MAX_IMAGE_EXPORT_DIMENSION;
    while (retryAttempts && (!url.length || url.length > MAX_IMAGE_EXPORT_BYTES)) {
        maxDimension = scaleMaxDimension(maxDimension, url.length, MAX_IMAGE_EXPORT_BYTES);
        const scalingMultiplier = dimensionScaler(width, height, maxDimension);

        try {
            url = canvas.toDataURL({
                format: makePNG ? 'png' : 'jpeg',
                quality: makePNG ? undefined : 0.7,
                multiplier: scalingMultiplier,
                left: leftOffset,
                top: topOffset,
                width: width,
                height: height,
            });
        } catch (error) {
            console.error(error);
            url = COMING_SOON_IMAGE;
            break;
        }

        retryAttempts--;
    }

    //set background to what it was before
    if (oldBackGroundColor) {
        canvas.setBackgroundColor(oldBackGroundColor, () => undefined);
    }

    return url;
};
